/**
 * Web 前端监控日志 API - 上报模块
 *
 * 模块说明：
 * 本模块用于 Web 管理后台前端的错误和性能日志**上报**。
 * 供 error-handler.ts 和 performance-monitor.ts 调用。
 *
 * ⚠️ 注意区分：
 * - 本模块 (web-logs.ts)：Web 前端日志上报
 *   API 路径：/api/v1/web-logs/
 *   调用方：error-handler.ts, performance-monitor.ts, request.ts
 *
 * - web-monitoring 模块：Web 前端日志**查询**（管理后台 UI）
 *   API 路径：/api/v1/web-logs/（相同）
 *   调用方：web-monitoring/views/*.vue
 *
 * - app-logs 模块：移动 App 日志上报和查询
 *   API 路径：/api/v1/app-logs/
 *
 * @author YSFL Team
 * @date 2026-01-24
 * @updated 2026-01-24 优化 API 路径命名（/web/ -> /web-logs/）
 */

import type {
  BatchUploadWebBehaviorForm as SharedBatchUploadWebBehaviorForm,
  BatchUploadWebErrorsForm as SharedBatchUploadWebErrorsForm,
  BatchUploadWebPerformanceForm as SharedBatchUploadWebPerformanceForm,
  WebBehaviorLogForm as SharedWebBehaviorLogForm,
  WebErrorLogForm as SharedWebErrorLogForm,
  WebLogUploadData as SharedWebLogUploadData,
  WebPerformanceLogForm as SharedWebPerformanceLogForm,
  WebBehaviorActionResultValue,
  WebBehaviorActionTypeValue,
  WebBehaviorLogQueryParams,
  WebBehaviorLogResponse,
  WebBehaviorStatisticsResponse,
  WebErrorLevelValue,
  WebErrorLogQueryParams,
  WebErrorLogResponse,
  WebErrorStatisticsResponse,
  WebErrorTypeValue,
  WebFunnelAnalysisQueryParams,
  WebFunnelAnalysisResponse,
  WebFunnelDefinition,
  WebFunnelDefinitionForm,
  WebFunnelDefinitionUpdateForm,
  WebHotPageItem,
  WebHotPagesQueryParams,
  WebInterruptionAnalysisResponse,
  WebInterruptionsQueryParams,
  WebMetricTypeValue,
  WebModuleUsageItem,
  WebModuleUsageQueryParams,
  WebPerformanceLogQueryParams,
  WebPerformanceLogResponse,
  WebPerformanceStatisticsResponse,
} from '@bayes-mes/shared';

import { requestClient, silentClient } from '../request';

export type WebErrorType = WebErrorTypeValue;
export type WebErrorLevel = WebErrorLevelValue;
export type WebMetricType = WebMetricTypeValue;
export type WebBehaviorActionType = WebBehaviorActionTypeValue;
export type WebBehaviorActionResult = WebBehaviorActionResultValue;
export type WebErrorLogForm = SharedWebErrorLogForm;
export type WebPerformanceLogForm = SharedWebPerformanceLogForm;
export type WebBehaviorLogForm = SharedWebBehaviorLogForm;
export type BatchUploadWebErrorsForm = SharedBatchUploadWebErrorsForm;
export type BatchUploadWebPerformanceForm = SharedBatchUploadWebPerformanceForm;
export type BatchUploadWebBehaviorForm = SharedBatchUploadWebBehaviorForm;

// 漏斗分析类型（Shared 导出）
export type {
  WebFunnelAnalysisQueryParams,
  WebFunnelAnalysisResponse,
  WebFunnelDefinition,
  WebFunnelDefinitionForm,
  WebFunnelDefinitionStep,
  WebFunnelDefinitionUpdateForm,
  WebFunnelStep,
  WebInterruptionAnalysisResponse,
  WebInterruptionsQueryParams,
} from '@bayes-mes/shared';

/** 上传结果数据（camelCase，后端 Resource 输出） */
export interface WebLogUploadData extends SharedWebLogUploadData {
  monitoringConfig?: Record<string, any>;
}

/**
 * 标准 API 响应格式（符合后端 ApiResponse trait）
 */
export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

/** 上传响应类型 */
export type WebLogUploadResponse = ApiResponse<WebLogUploadData>;

// ============================================================================
// 响应类型（查询接口，camelCase）
// ============================================================================

// ============================================================================
// API 函数
// ============================================================================

/**
 * Web 前端日志 API 路径
 *
 * 路径说明：/api/v1/web-logs/ 是 Web 前端日志专用路径
 * ⚠️ 区分：App 日志使用 /api/v1/app-logs/
 */
const WEB_LOGS_API = {
  /** Web 前端错误日志 */
  ERRORS: '/api/v1/web-logs/errors',
  /** Web 前端错误统计 */
  ERRORS_STATISTICS: '/api/v1/web-logs/errors/statistics',
  /** Web 前端性能日志 */
  PERFORMANCE: '/api/v1/web-logs/performance',
  /** Web 前端性能统计 */
  PERFORMANCE_STATISTICS: '/api/v1/web-logs/performance/statistics',
  /** Web 前端行为日志 */
  BEHAVIOR: '/api/v1/web-logs/behavior',
  /** Web 前端行为统计 */
  BEHAVIOR_STATISTICS: '/api/v1/web-logs/behavior/statistics',
  /** Web 前端热门页面 */
  BEHAVIOR_HOT_PAGES: '/api/v1/web-logs/behavior/hot-pages',
  /** Web 前端模块使用统计 */
  BEHAVIOR_MODULES: '/api/v1/web-logs/behavior/modules',
  /** Web sourcemap 上传（构建发布环节） */
  SOURCEMAPS: '/api/v1/web-logs/sourcemaps',
  /** Web 压缩堆栈还原 */
  SOURCEMAPS_RESOLVE: '/api/v1/web-logs/sourcemaps/resolve',
};

/**
 * Web 前端日志 API
 *
 * 用途：供 error-handler.ts 和 performance-monitor.ts 调用上报日志
 */
export const webLogsApi = {
  /**
   * 批量上传 Web 前端错误日志
   * POST /api/v1/web-logs/errors
   * 免鉴权接口
   *
   * ⚠️ 使用 silentClient：失败时不提示用户，静默处理
   */
  batchUploadErrors(data: BatchUploadWebErrorsForm) {
    return silentClient.post<WebLogUploadData>(WEB_LOGS_API.ERRORS, data);
  },

  /**
   * 批量上传 Web 前端性能日志
   * POST /api/v1/web-logs/performance
   * 免鉴权接口
   *
   * ⚠️ 使用 silentClient：失败时不提示用户，静默处理
   */
  batchUploadPerformance(data: BatchUploadWebPerformanceForm) {
    return silentClient.post<WebLogUploadData>(WEB_LOGS_API.PERFORMANCE, data);
  },

  /**
   * 批量上传 Web 前端行为日志
   * POST /api/v1/web-logs/behavior
   * 免鉴权接口
   */
  batchUploadBehavior(data: BatchUploadWebBehaviorForm) {
    return silentClient.post<WebLogUploadData>(WEB_LOGS_API.BEHAVIOR, data);
  },

  batchUploadBehaviorLogs(data: BatchUploadWebBehaviorForm) {
    return silentClient.post<WebLogUploadData>(WEB_LOGS_API.BEHAVIOR, data);
  },

  /**
   * 获取 Web 前端错误日志列表
   * GET /api/v1/web-logs/errors
   * 需要鉴权
   */
  getErrorLogs(params?: WebErrorLogQueryParams) {
    return requestClient.get<{
      items: WebErrorLogResponse[];
      pagination: {
        currentPage: number;
        lastPage: number;
        perPage: number;
        total: number;
      };
    }>(WEB_LOGS_API.ERRORS, { params });
  },

  /**
   * 获取 Web 前端性能日志列表
   * GET /api/v1/web-logs/performance
   * 需要鉴权
   */
  getPerformanceLogs(params?: WebPerformanceLogQueryParams) {
    return requestClient.get<{
      items: WebPerformanceLogResponse[];
      pagination: {
        currentPage: number;
        lastPage: number;
        perPage: number;
        total: number;
      };
    }>(WEB_LOGS_API.PERFORMANCE, { params });
  },

  /**
   * 获取错误统计
   * GET /api/v1/web/errors/statistics
   * 需要鉴权
   */
  getErrorStatistics(params?: { end_time?: string; start_time?: string }) {
    return requestClient.get<WebErrorStatisticsResponse>(
      WEB_LOGS_API.ERRORS_STATISTICS,
      { params },
    );
  },

  /**
   * 获取性能统计
   * GET /api/v1/web/performance/statistics
   * 需要鉴权
   */
  getPerformanceStatistics(params?: {
    end_time?: string;
    start_time?: string;
  }) {
    return requestClient.get<WebPerformanceStatisticsResponse>(
      WEB_LOGS_API.PERFORMANCE_STATISTICS,
      { params },
    );
  },

  /**
   * 获取 Web 前端行为日志列表
   */
  getBehaviorLogs(params?: WebBehaviorLogQueryParams) {
    return requestClient.get<{
      items: WebBehaviorLogResponse[];
      pagination: {
        currentPage: number;
        lastPage: number;
        perPage: number;
        total: number;
      };
    }>(WEB_LOGS_API.BEHAVIOR, { params });
  },

  /**
   * 获取行为统计
   */
  getBehaviorStatistics(params?: { end_time?: string; start_time?: string }) {
    return requestClient.get<WebBehaviorStatisticsResponse>(
      WEB_LOGS_API.BEHAVIOR_STATISTICS,
      { params },
    );
  },

  /**
   * 获取热门页面
   */
  getHotPages(params?: WebHotPagesQueryParams) {
    return requestClient.get<WebHotPageItem[]>(
      WEB_LOGS_API.BEHAVIOR_HOT_PAGES,
      { params },
    );
  },

  /**
   * 获取模块使用统计
   */
  getModuleUsage(params?: WebModuleUsageQueryParams) {
    return requestClient.get<WebModuleUsageItem[]>(
      WEB_LOGS_API.BEHAVIOR_MODULES,
      { params },
    );
  },

  /**
   * 还原压缩堆栈为源码位置（sourcemap）
   * POST /api/v1/web-logs/sourcemaps/resolve
   */
  resolveStack(data: { stack: string; version?: string }) {
    return requestClient.post<{
      resolved_frames: number;
      resolved_stack: string;
      total_frames: number;
      version: string;
    }>(WEB_LOGS_API.SOURCEMAPS_RESOLVE, data);
  },

  // ======================================================================
  // 漏斗分析（类型来自 @bayes-mes/shared）
  // ======================================================================

  /**
   * 漏斗定义列表
   * GET /api/v1/web-logs/funnel/list
   */
  getFunnelList() {
    return requestClient.get<WebFunnelDefinition[]>(
      '/api/v1/web-logs/funnel/list',
    );
  },

  /**
   * 漏斗转化分析
   * GET /api/v1/web-logs/funnel/analysis
   */
  getFunnelAnalysis(params: WebFunnelAnalysisQueryParams) {
    return requestClient.get<WebFunnelAnalysisResponse>(
      '/api/v1/web-logs/funnel/analysis',
      { params },
    );
  },

  /**
   * 操作中断分析
   * GET /api/v1/web-logs/funnel/interruptions
   */
  getFunnelInterruptions(params?: WebInterruptionsQueryParams) {
    return requestClient.get<WebInterruptionAnalysisResponse>(
      '/api/v1/web-logs/funnel/interruptions',
      { params },
    );
  },

  /**
   * 创建漏斗定义
   * POST /api/v1/web-logs/funnel
   */
  createFunnel(data: WebFunnelDefinitionForm) {
    return requestClient.post<WebFunnelDefinition>(
      '/api/v1/web-logs/funnel',
      data,
    );
  },

  /**
   * 更新漏斗定义
   * PUT /api/v1/web-logs/funnel/{id}
   */
  updateFunnel(id: number, data: WebFunnelDefinitionUpdateForm) {
    return requestClient.put<WebFunnelDefinition>(
      `/api/v1/web-logs/funnel/${id}`,
      data,
    );
  },

  /**
   * 删除漏斗定义
   * DELETE /api/v1/web-logs/funnel/{id}
   */
  deleteFunnel(id: number) {
    return requestClient.delete(`/api/v1/web-logs/funnel/${id}`);
  },
};

export default webLogsApi;
